import type { ReactNode } from 'react';
import { Overlay } from '../../../ui/Overlay';
import type { Session } from '../../../shell/bridge';
import { assigneeLabel } from './helpers';
import type { ChoreItem } from './types';

/** Week/schedule views (card QR9phRSRQHNx, desktop uplift A6 card
 *  R0Yth1PtWGRg): the toggle, the shared body, the rail and the drawer. */
export function WeekToggle({ weekOpen, onOpen }: { weekOpen: boolean; onOpen: () => void }) {
  return (
    <button
      type="button"
      className="pear-btn ch-week-toggle"
      id="ch-week-toggle"
      data-testid="ch-week-toggle"
      aria-haspopup="dialog"
      aria-expanded={weekOpen}
      onClick={onOpen}
    >
      This week
    </button>
  );
}

export function WeekBody({ weekDay, setWeekDay, weekWho, setWeekWho, weekDone, setWeekDone, weekDays, weekFiltered, weekDayKey, me, kind, setKind, kinds, kindOf }: {
  weekDay: string;
  setWeekDay: (v: string) => void;
  weekWho: string;
  setWeekWho: (v: string) => void;
  weekDone: string;
  setWeekDone: (v: string) => void;
  weekDays: string[];
  weekFiltered: ChoreItem[];
  weekDayKey: (c: ChoreItem) => string;
  me: Session | null;
  /** Kind filter — only rendered when the caller passes kinds (the Week tab
      mixes household rows and dates; nothing else uses this body any more). */
  kind?: string;
  setKind?: (v: string) => void;
  kinds?: string[];
  kindOf?: (c: ChoreItem) => string | undefined;
}) {
  /* The week's body — the three filters and the day | task | responsible |
     complete table (or its empty hint) — is shared verbatim by the drawer
     below the token and the rail at it (desktop uplift A6, card
     R0Yth1PtWGRg). Exactly ONE of the two is rendered per width, so the
     testids below never appear twice and a walk can never resolve a filter
     to a hidden copy (the contract InboxList ships). */
  return (
    <>
      <div className="ch-week-filters" role="group" aria-label="Filter the week">
        <label className="ch-week-filter">
          <span>Day</span>
          <select
            className="pear-chat-input"
            data-testid="ch-week-filter-day"
            value={weekDay}
            onChange={e => setWeekDay(e.target.value)}
          >
            <option value="all">All days</option>
            {weekDays.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </label>
        <label className="ch-week-filter">
          <span>Responsible</span>
          <select
            className="pear-chat-input"
            data-testid="ch-week-filter-who"
            value={weekWho}
            onChange={e => setWeekWho(e.target.value)}
          >
            <option value="all">Everyone</option>
            <option value="Me">Me</option>
            <option value="Partner">Partner</option>
            <option value="Anyone">Anyone</option>
          </select>
        </label>
        <label className="ch-week-filter">
          <span>Complete</span>
          <select
            className="pear-chat-input"
            data-testid="ch-week-filter-done"
            value={weekDone}
            onChange={e => setWeekDone(e.target.value)}
          >
            <option value="all">All</option>
            <option value="open">Not done</option>
            <option value="done">Done</option>
          </select>
        </label>
        {kinds && setKind && kind !== undefined && (
          <label className="ch-week-filter">
            <span>Kind</span>
            <select
              className="pear-chat-input"
              data-testid="ch-week-filter-kind"
              value={kind}
              onChange={e => setKind(e.target.value)}
            >
              <option value="all">All kinds</option>
              {kinds.map(k => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {weekFiltered.length === 0 ? (
        <p className="rq-empty-hint ch-week-empty" data-testid="ch-week-empty">
          Nothing matches — the week is clear on that filter.
        </p>
      ) : (
        <table className="ch-week-table" data-testid="ch-week-table">
          <thead>
            <tr>
              <th scope="col">Day</th>
              <th scope="col">Task</th>
              <th scope="col">Responsible</th>
              <th scope="col">Complete</th>
            </tr>
          </thead>
          <tbody>
            {weekFiltered.map(c => {
              const kind = kindOf?.(c);
              return (
              <tr key={c.choreId} data-testid="ch-week-row" className={c.status === 'done' ? 'is-done' : ''} data-kind={kind ?? ''}>
                <td>{weekDayKey(c)}</td>
                <td>
                  {kind && <span className={'life-kind-chip life-kind-' + kind.toLowerCase()}>{kind}</span>}
                  {c.title}
                </td>
                <td>{assigneeLabel(c, me)}</td>
                <td>
                  {c.status === 'done' ? (
                    <span className="ch-week-done" aria-label="Done">✓</span>
                  ) : (
                    <span className="ch-week-todo" aria-label="Not done">—</span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}

/* The rail (A6): the SAME weekBody the drawer would have shown —
   filters and table verbatim — standing beside the list. Layout,
   not an overlay: no backdrop, no Esc, no focus trap; it is simply
   where the week lives now. */
export function WeekRail({ children }: { children: ReactNode }) {
  return (
    <aside
      className="ch-week-rail"
      data-testid="ch-week-rail"
      aria-label="This week"
      tabIndex={-1}
    >
      <div className="ch-week-head">
        <h3 className="ch-week-title" id="ch-week-rail-title">
          This week
        </h3>
      </div>
      {children}
    </aside>
  );
}

/* Week summary drawer (card QR9phRSRQHNx) — day | task | responsible |
   complete, filterable on any of the three. Escape or the backdrop
   closes it, the same contract every other drawer in the app keeps.
   Below the A6 token only: at the token the rail above carries the
   same body, so rendering both would put every filter testid in the
   DOM twice. */
export function WeekDrawer({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  return (
    <Overlay
      open={open}
      onClose={onClose}
      className="ch-week-overlay"
      panelClassName="ch-week-drawer"
      labelledBy="ch-week-title"
      panelTestId="ch-week-drawer"
    >
      <div className="ch-week-head">
        <h3 className="ch-week-title" id="ch-week-title">
          This week
        </h3>
        <button
          type="button"
          className="pear-btn ch-week-close"
          aria-label="Close week summary"
          onClick={onClose}
        >
          ✕
        </button>
      </div>

      {children}
    </Overlay>
  );
}
